// src/components/admin/HumanRequestAlert.jsx
//
// "Human Requested" alert on every page (owner, Nov 3 2026): while an admin or
// specialist is signed in, any visitor waiting for a person (Havlo chats, and the
// in-site chat's needsHumanAttention) shows a banner with a link to the Support
// Queue, plays a sound and sends a browser notification. Hidden on the Support
// Queue page itself, which has its own alerts.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { collection, onSnapshot, query, where, orderBy, limit, Timestamp } from "firebase/firestore";
import { db } from "../../firebaseConfig";
import { useAuth } from "../../context/AuthContext";
import { useLiveChats } from "../../hooks/useLiveChats";
import { BellRing, MessageSquareText, X } from "lucide-react";

const QUEUE = "/admin/live-support";
const TICKETS = "/specialist/support-queue";
// Short two-tone chime, generated so no audio file is needed.
function chime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    [880, 1175].forEach((f, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.frequency.value = f;
      g.gain.setValueAtTime(0.18, ctx.currentTime + i * 0.22);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.22 + 0.2);
      o.connect(g).connect(ctx.destination);
      o.start(ctx.currentTime + i * 0.22);
      o.stop(ctx.currentTime + i * 0.22 + 0.22);
    });
  } catch { /* sound is a nicety */ }
}

function notify(title, body, tag) {
  if (!("Notification" in window)) return;
  const show = () => new Notification(title, { body, tag, requireInteraction: true });
  if (Notification.permission === "granted") show();
  else if (Notification.permission === "default") Notification.requestPermission().then((p) => p === "granted" && show());
}

// New feedback tickets (the Feedback tab, course review, chat feedback):
// chime + notification + a banner linking to the Ticket Queue. Only tickets
// submitted after this page opened count, so old ones never re-alert.
function FeedbackWatcher() {
  const { pathname } = useLocation();
  const [fresh, setFresh] = useState([]);
  useEffect(() => {
    const since = Timestamp.now();
    const q = query(collection(db, "feedback"), where("dateSubmitted", ">", since), orderBy("dateSubmitted", "desc"), limit(10));
    return onSnapshot(q, (snap) => {
      const added = snap.docChanges().filter((c) => c.type === "added").map((c) => ({ id: c.doc.id, ...c.doc.data() }));
      if (!added.length) return;
      setFresh((prev) => [...added, ...prev].slice(0, 10));
      chime();
      const who = added[0].submittedByName || added[0].submittedBy || "Someone";
      notify("New feedback ticket", `${who}: ${(added[0].message || added[0].feedbackType || "").slice(0, 120)}`, "feedback-ticket");
    }, () => {});
  }, []);
  if (!fresh.length || pathname.endsWith("/support-queue")) return null;
  const first = fresh[0];
  return (
    <div role="status" className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] w-[min(560px,calc(100vw-2rem))] flex items-center gap-3 rounded-2xl border border-cyan-300 bg-cyan-50 text-cyan-950 dark:border-cyan-500/40 dark:bg-slate-900 dark:text-cyan-100 shadow-xl px-4 py-3">
      <MessageSquareText size={20} className="flex-none" />
      <div className="flex-1 min-w-0 text-sm">
        <p className="font-black">{fresh.length === 1 ? "New feedback ticket" : `${fresh.length} new feedback tickets`}</p>
        <p className="truncate opacity-80">{first.submittedByName || first.submittedBy || "Someone"}: {first.message || first.feedbackType}</p>
      </div>
      <Link to={TICKETS} onClick={() => setFresh([])} className="flex-none px-3 py-2 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-black">Open Ticket Queue</Link>
      <button type="button" onClick={() => setFresh([])} className="flex-none p-1.5 rounded-lg hover:bg-cyan-100 dark:hover:bg-slate-800" aria-label="Dismiss">
        <X size={16} />
      </button>
    </div>
  );
}

function Watcher() {
  const { pathname } = useLocation();
  const { chats } = useLiveChats(); // Havlo + site chats (site ones are read below)
  const [siteChats, setSiteChats] = useState([]);
  const [dismissedAt, setDismissedAt] = useState(0);
  const seen = useRef(new Set());

  // In-site chat (liveChats) visitors who asked for a person
  useEffect(() => {
    const q = query(collection(db, "liveChats"), where("needsHumanAttention", "==", true));
    return onSnapshot(q, (snap) => {
      setSiteChats(snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((c) => c.status === "active" && !c.assignedTo));
    }, () => {});
  }, []);

  const waiting = useMemo(() => {
    const havlo = (chats || []).filter((c) => c.source === "havlo" && c.needsHumanAttention).map((c) => ({ id: `h-${c.id}`, name: c.visitorName || "A visitor" }));
    const site = siteChats.map((c) => ({ id: `s-${c.id}`, name: c.visitorName || c.userEmail || "A visitor" }));
    return [...havlo, ...site];
  }, [chats, siteChats]);

  // New requests: sound + browser notification + bring the banner back
  useEffect(() => {
    const fresh = waiting.filter((w) => !seen.current.has(w.id));
    if (!fresh.length) return;
    fresh.forEach((w) => seen.current.add(w.id));
    setDismissedAt(0);
    if (!window.location.pathname.endsWith("/live-support")) chime(); // the Support Queue plays its own sound
    if ("Notification" in window) {
      const show = () => new Notification("Human requested", {
        body: `${fresh.map((w) => w.name).join(", ")} ${fresh.length > 1 ? "are" : "is"} waiting for a person.`,
        tag: "human-requested",
        requireInteraction: true,
      });
      if (Notification.permission === "granted") show();
      else if (Notification.permission === "default") Notification.requestPermission().then((p) => p === "granted" && show());
    }
  }, [waiting]);

  if (!waiting.length || dismissedAt || pathname.endsWith("/live-support")) return null;
  return (
    <div role="alert" className="fixed top-3 left-1/2 -translate-x-1/2 z-[60] w-[min(560px,calc(100vw-2rem))] flex items-center gap-3 rounded-2xl border border-red-300 bg-red-50 text-red-900 dark:border-red-500/40 dark:bg-red-950 dark:text-red-100 shadow-xl px-4 py-3">
      <BellRing size={20} className="flex-none animate-pulse" />
      <div className="flex-1 min-w-0 text-sm">
        <p className="font-black">Human requested</p>
        <p className="truncate opacity-80">{waiting.length === 1 ? `${waiting[0].name} is waiting for a person` : `${waiting.length} visitors are waiting for a person`}</p>
      </div>
      <Link to={QUEUE} className="flex-none px-3 py-2 rounded-xl bg-red-600 hover:bg-red-500 text-white text-xs font-black">Open Support Queue</Link>
      <button type="button" onClick={() => setDismissedAt(Date.now())} className="flex-none p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900" aria-label="Hide until the next request">
        <X size={16} />
      </button>
    </div>
  );
}

export default function HumanRequestAlert() {
  const { currentUser, isAdmin } = useAuth();
  if (!currentUser || !isAdmin) return null; // alerts are for admins only
  return (
    <>
      <Watcher />
      <FeedbackWatcher />
    </>
  );
}
